import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService, ToastType } from '@app/components/toast';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import {
  Challenge,
  ChallengeService,
  EditChallengePayload,
} from '@app/services/challenge/challenge.service';
import { Logger } from '@app/services/logger/logger.service';
import { SaveChangesService } from '@app/services/save-changes/save-changes.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { catchError, of } from 'rxjs';
import { SaveChangesConfirmationComponent } from '../../../../shared/components/save-changes-confirmation/save-changes-confirmation.component';
import {
  StepChallengeFormComponent,
  StepChallengeFormValue,
} from '../step-challenge-form/step-challenge-form.component';

@Component({
  selector: 'app-step-challenge-edit',
  templateUrl: './step-challenge-edit.component.html',
  standalone: true,
  imports: [StepChallengeFormComponent, SaveChangesConfirmationComponent],
})
export class StepChallengeEditComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly challengeService = inject(ChallengeService);
  private readonly tenantService = inject(AdminTenantService);
  private readonly toastService = inject(ToastService);
  private readonly translatorService = inject(TranslatorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);
  private readonly saveChanges = inject(SaveChangesService);

  private readonly challengeId = this.route.snapshot.paramMap.get('id') ?? '';

  isSaving = signal(false);
  isReadOnly = signal(true);
  challenge = signal<Challenge>(this.route.snapshot.data['challenge']);
  readonly goBackNavigate = ['challenges'];

  onEditRequested(): void {
    this.isReadOnly.set(false);
  }

  onSave(value: StepChallengeFormValue): void {
    const tenantId = this.tenantService.getTenantId();
    if (!tenantId) {
      this.logger.error('Missing tenant ID for update');
      this.toastService.add(
        this.translatorService.translate('challenges.challengeCouldNotBeSaved'),
        ToastType.ERROR
      );
      return;
    }

    this.isSaving.set(true);

    const dto: EditChallengePayload = {
      id: this.challengeId,
      author: tenantId,
      type: 'dailySteps',
      name: value.title,
      description: value.description,
      goal: value.goal,
      startDate: value.startDate,
      endDate: value.endDate,
      enrollmentStartDate: value.enrollmentStartDate,
      joinInProgress: value.joinInProgress,
      teamAssignmentType: value.teamAssignmentType,
      teamAssignmentValue: value.teamAssignmentValue,
    };

    this.challengeService
      .editChallenge(dto)
      .pipe(
        catchError((err) => {
          this.logger.error('Error updating challenge');
          this.logger.error(JSON.stringify(err, null, 2));
          this.toastService.add(
            this.translatorService.translate(
              'challenges.challengeCouldNotBeSaved'
            ),
            ToastType.ERROR
          );
          this.isSaving.set(false);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((challenge) => {
        if (!challenge) return;
        this.challenge.set(challenge);
        this.isReadOnly.set(true);
        this.isSaving.set(false);
        this.saveChanges.setHasChanges(false);
        this.toastService.add(
          this.translatorService.translate('challenges.challengeSaved')
        );
      });
  }

  goBack(): void {
    this.router.navigate(this.goBackNavigate);
  }
}
